"use client";

import React, { useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import componentMapping from "@/components/PageHome/componentMapping";
import { DeregisterRefType, RefsObjectType, RegisterRefType, serverReturnType } from "@/components/PageHome/interfaces";
import HomeSelector from "@/components/PageHome/redux/selectors/homeData";
import { useSyncHomeData } from "@/components/PageHome/useSyncHomeData";
import {
  COMMON_COMPONENT_TYPES,
  COMPONENT_TYPES,
  EVENT_NAMES,
  NON_ONBOARDED_STATUS,
  ONBOARDING_STATUS,
} from "@/helpers/constants";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import signedInStateSelector from "@/redux/slice/auth/selectors/signedInState";
import { SignedinState } from "@/redux/slice/auth/enums/SignedInState";
import currentPatientSelector from "@/redux/slice/auth/selectors/currentPatient";
import { pageViewEvent } from "@/helpers/TSDEvents";
import { setReferer } from "@/redux/slice/global/slice";
import { getPatientAge } from "@/redux/slice/auth/utils/getPatientAge";
import styles from "./PageHome.module.scss";

interface HomePageProps {
  pageInfo: serverReturnType;
}

const HomePage: React.FC<HomePageProps> = ({ pageInfo }) => {
  const homeInfo = pageInfo?.homeData;
  const dispatch = useAppDispatch();

  const searchParams = useSearchParams();
  const signedInStatus = useAppSelector(signedInStateSelector);
  const currentPatientData = useAppSelector(currentPatientSelector);

  const elementRefs = useRef<RefsObjectType>({});

  const registerRef: RegisterRefType = (key, element) => {
    elementRefs.current[key] = element;
  };

  // Function to deregister the ref (cleanup)
  const deregisterRef: DeregisterRefType = (key) => {
    delete elementRefs.current[key]; // Deregister the ref when the child unmounts
  };

  useSyncHomeData({ homeInfo });
  const homeData = useAppSelector(HomeSelector);

  const firstFoldData = homeData?.content?.find(({ ComponentType }) => ComponentType === COMPONENT_TYPES.FirstFold);

  const extras = {
    elementRefs,
    registerRef,
    deregisterRef,
    userOnBoarded: pageInfo?.userOnBoarded,
    userAgent: pageInfo?.ua,
    productInfo: homeInfo?.productInfo,
    currentSpeciality: homeInfo?.currentSpeciality,
    doctors: homeInfo?.doctorsArray,
    firstFoldData,
    diagnostics: homeInfo?.diagnosticsArray,
  };

  useEffect(() => {
    dispatch(setReferer(pageInfo?.referer));

    if (signedInStatus !== SignedinState.UNRESOLVED) {
      const isUserLoggedin = signedInStatus === SignedinState.SIGNEDIN;
      pageViewEvent({
        page: "home",
        is_loggedin: isUserLoggedin ? true : false,
        momverse_stage: pageInfo?.userOnBoarded ? (ONBOARDING_STATUS[homeInfo?.type] ?? "") : NON_ONBOARDED_STATUS,
        platform: pageInfo?.ua?.isDesktop ? "Desktop" : "Mobile",
        age: getPatientAge(currentPatientData),
        nav_src: pageInfo?.referer || "direct",
        utm_source: searchParams.get("utm_source") ?? "",
        utm_medium: searchParams.get("utm_medium") ?? "",
        utm_campaign: searchParams.get("utm_campaign") ?? "",
        firstname: isUserLoggedin && currentPatientData?.firstName ? currentPatientData?.firstName : "",
      });
    }
  }, [signedInStatus]);

  useEffect(() => {
    // Scroll to top on page load
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {homeData?.content?.map((data: any, index: number) => {
        const type = data?.ComponentType;

        return (
          <React.Fragment key={index}>
            {type === COMMON_COMPONENT_TYPES.NavBar ? (
              componentMapping({ data, homeType: homeData.type, extras: { ...extras, assetPosition: index + 1 } })
            ) : (
              <div className={styles.section}>
                {componentMapping({ data, homeType: homeData.type, extras: { ...extras, assetPosition: index + 1 } })}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default HomePage;
